import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Home, 
  MessageSquare, 
  Mail, 
  Plus,
  Search,
  Filter,
  Send,
  Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function Messages() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('all');
  const [smsMessages, setSmsMessages] = useState([]);
  const [emails, setEmails] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Compose modal
  const [composeModalOpen, setComposeModalOpen] = useState(false);
  const [composeType, setComposeType] = useState('sms'); // sms or email
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  const [requestConfirmation, setRequestConfirmation] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadMessages();
  }, []);

  // Prefill compose modal when navigated from a Quick Action (ItemDetail)
  useEffect(() => {
    const compose = location.state?.compose;
    if (compose) {
      setComposeType(compose.type === 'email' ? 'email' : 'sms');
      setComposeTo(compose.to || '');
      setComposeModalOpen(true);
    }
  }, [location.state]);

  const combineMessages = useCallback(() => {
    let combined = [];

    // Add SMS
    if (activeTab === 'all' || activeTab === 'sms') {
      smsMessages.forEach(msg => {
        combined.push({
          ...msg,
          type: 'sms',
          preview: msg.message_body,
          date: msg.sent_at
        });
      });
    }

    // Add emails
    if (activeTab === 'all' || activeTab === 'email') {
      emails.forEach(email => {
        combined.push({
          ...email,
          type: 'email',
          preview: email.subject,
          date: email.sent_at
        });
      });
    }

    // Filter by search
    if (searchQuery) {
      combined = combined.filter(msg => 
        msg.phone_number?.includes(searchQuery) ||
        msg.to_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        msg.message_body?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        msg.subject?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort by date
    combined.sort((a, b) => new Date(b.date) - new Date(a.date));

    setAllMessages(combined);
  }, [smsMessages, emails, searchQuery, activeTab]);

  useEffect(() => {
    combineMessages();
  }, [combineMessages]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const [smsRes, emailRes] = await Promise.all([
        axios.get(`${API}/messages/sms`, { withCredentials: true }),
        axios.get(`${API}/messages/emails`, { withCredentials: true })
      ]);

      setSmsMessages(smsRes.data || []);
      setEmails(emailRes.data || []);
    } catch (error) {
      // Error logged: 'Failed to load messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleCompose = async (e) => {
    e.preventDefault();
    try {
      setSending(true);

      if (composeType === 'sms') {
        const res = await axios.post(`${API}/messages/sms/send`, {
          phone_number: composeTo,
          message_body: composeMessage,
          request_confirmation: requestConfirmation
        }, { withCredentials: true });
        if (res.data.status === 'failed') {
          toast.error(`SMS failed to send: ${res.data.error || 'unknown error'}`);
          loadMessages();
          return;
        }
        toast.success('SMS sent successfully');
      } else {
        const res = await axios.post(`${API}/messages/emails/send`, {
          to_email: composeTo,
          subject: composeSubject,
          body: composeMessage
        }, { withCredentials: true });
        if (res.data.status === 'failed') {
          toast.error(`Email failed to send: ${res.data.error || 'unknown error'}`);
          loadMessages();
          return;
        }
        toast.success('Email sent successfully');
      }

      setComposeModalOpen(false);
      setComposeTo('');
      setComposeSubject('');
      setComposeMessage('');
      setRequestConfirmation(false);
      loadMessages();
    } catch (error) {
      toast.error(`Failed to send ${composeType}`);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center">
        <p className="text-[#474747]">Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E9E9E9]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#474747]"
            >
              <Home className="w-4 h-4" />
              Back to Dashboard
            </button>
            
            <Button
              onClick={() => setComposeModalOpen(true)}
              className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Message
            </Button>
          </div>

          <h1 className="text-3xl font-heading font-semibold text-[#474747] mb-4">Messages</h1>

          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search messages..."
                className="w-full pl-10 pr-4 py-2 border border-[#E9E9E9] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#C6E76C]/50"
              />
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'all'
                ? 'bg-[#C6E76C] text-[#474747]'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-[#E9E9E9]'
            }`}
          >
            All ({smsMessages.length + emails.length})
          </button>
          <button
            onClick={() => setActiveTab('sms')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'sms'
                ? 'bg-[#C6E76C] text-[#474747]'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-[#E9E9E9]'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            SMS ({smsMessages.length})
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'email'
                ? 'bg-[#C6E76C] text-[#474747]'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-[#E9E9E9]'
            }`}
          >
            <Mail className="w-4 h-4" />
            Email ({emails.length})
          </button>
        </div>

        {/* Messages List */}
        <div className="bg-white rounded-xl border border-[#E9E9E9]">
          {allMessages.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500">No messages found</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E9E9E9]">
              {allMessages.map((msg) => (
                <div
                  key={msg.id}
                  className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.type === 'sms' ? 'bg-green-100' : 'bg-blue-100'
                      }`}>
                        {msg.type === 'sms' ? (
                          <MessageSquare className="w-5 h-5 text-green-600" />
                        ) : (
                          <Mail className="w-5 h-5 text-blue-600" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {msg.type === 'sms' ? (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <p className="text-sm font-medium text-[#474747]">{msg.phone_number}</p>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <p className="text-sm font-medium text-[#474747]">{msg.to_email}</p>
                            </div>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            msg.direction === 'outbound' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {msg.direction === 'outbound' ? 'Sent' : 'Received'}
                          </span>
                        </div>

                        {msg.type === 'email' && msg.subject && (
                          <p className="text-sm font-medium text-[#474747] mb-1">{msg.subject}</p>
                        )}

                        <p className="text-sm text-gray-600 line-clamp-2">
                          {msg.message_body || msg.body}
                        </p>

                        {msg.status && (
                          <div className="mt-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              msg.status === 'sent' ? 'bg-green-100 text-green-700' :
                              msg.status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {msg.status}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Date */}
                    <div className="text-xs text-gray-500 ml-4">
                      {formatDate(msg.date)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Compose Modal */}
      <Dialog open={composeModalOpen} onOpenChange={setComposeModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading font-semibold text-[#474747]">
              New Message
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCompose} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setComposeType('sms')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    composeType === 'sms'
                      ? 'bg-[#C6E76C] text-[#474747]'
                      : 'bg-white text-gray-600 border border-[#E9E9E9]'
                  }`}
                >
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  SMS
                </button>
                <button
                  type="button"
                  onClick={() => setComposeType('email')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    composeType === 'email'
                      ? 'bg-[#C6E76C] text-[#474747]'
                      : 'bg-white text-gray-600 border border-[#E9E9E9]'
                  }`}
                >
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">
                {composeType === 'sms' ? 'Phone Number' : 'Email Address'} *
              </label>
              <input
                type={composeType === 'sms' ? 'tel' : 'email'}
                required
                value={composeTo}
                onChange={(e) => setComposeTo(e.target.value)}
                placeholder={composeType === 'sms' ? '+61 4XX XXX XXX' : 'seller@example.com'}
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
              />
            </div>

            {composeType === 'email' && (
              <div>
                <label className="text-sm font-medium text-[#474747] block mb-2">Subject *</label>
                <input
                  type="text"
                  required
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  placeholder="Message subject"
                  className="w-full p-2 border border-[#E9E9E9] rounded-md"
                />
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-[#474747] block mb-2">Message *</label>
              <textarea
                required
                value={composeMessage}
                onChange={(e) => setComposeMessage(e.target.value)}
                rows={composeType === 'sms' ? 6 : 10}
                placeholder="Type your message..."
                className="w-full p-2 border border-[#E9E9E9] rounded-md"
              />
              {composeType === 'sms' && (
                <p className="text-xs text-gray-500 mt-1">
                  {composeMessage.length}{requestConfirmation ? ' + 21 (YES/NO prompt)' : ''} characters
                </p>
              )}
            </div>

            {composeType === 'sms' && (
              <label className="flex items-start gap-3 p-3 rounded-lg border border-[#E9E9E9] bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors" data-testid="request-confirmation-label">
                <input
                  type="checkbox"
                  checked={requestConfirmation}
                  onChange={(e) => setRequestConfirmation(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#C6E76C]"
                  data-testid="request-confirmation-checkbox"
                />
                <div>
                  <p className="text-sm font-medium text-[#474747]">Ask seller to reply YES or NO</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Appends "Reply YES or NO." to the message. Their reply will be forwarded to your phone with a link back to what you asked.
                  </p>
                </div>
              </label>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={sending}
                className="flex-1 bg-[#C6E76C] hover:bg-[#b5d65f] text-[#474747] font-semibold"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sending...' : 'Send Message'}
              </Button>
              <Button
                type="button"
                onClick={() => setComposeModalOpen(false)}
                variant="outline"
                className="border-[#E9E9E9]"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
